import React from 'react';
import { dateTime, relTime } from '../lib/data';
import Pipeline from './Pipeline';
import Timeline from './Timeline';
import AgentChip from './AgentChip';
import Confidence from './Confidence';
import StatusPill from './StatusPill';
import { Cpu, GitBranch, Bot, DollarSign } from 'lucide-react';

const usd4 = (n) => `$${(n || 0).toFixed(4)}`;
const usd = (n) => `$${(n || 0).toFixed(2)}`;
const TONE_TEXT = { green: 'text-emerald-600', amber: 'text-amber-600', red: 'text-rose-600', violet: 'text-violet-600', sky: 'text-sky-600', default: 'text-slate-500' };

// The cohesive per-appointment execution view: pipeline stages, the ordered
// chain of every agent's response (a timeline), and a per-agent profile with
// each agent's response + confidence + token cost.
export default function AgentChain({ view }) {
  if (!view) return null;
  const chain = view.agentChain || [];
  const stages = view.stages || [];
  const agents = view.agents || [];
  const cost = view.cost || {};

  const events = chain.map((e) => ({
    tone: e.tone || 'default',
    icon: Bot,
    title: (
      <div className="flex items-center gap-2 flex-wrap">
        <AgentChip agent={e.key} label={e.agentLabel} size="xs" />
        <span className="text-sm text-slate-900 font-medium">{e.title}</span>
        {e.logged && <span className="pill pill-violet">logged</span>}
      </div>
    ),
    detail: (
      <div className="space-y-1">
        {e.detail && <div className="text-xs text-slate-600">{e.detail}</div>}
        <div className="flex items-center gap-3 text-[11px] text-slate-400">
          {e.confidence != null && <Confidence value={e.confidence} showLabel={false} />}
          {e.costUsd != null && <span className="tnum" title={`${((e.tokensInput || 0) + (e.tokensOutput || 0)).toLocaleString()} tokens · ${e.model || ''}`}><DollarSign className="inline h-3 w-3 -mt-0.5" />{usd4(e.costUsd).slice(1)}</span>}
        </div>
      </div>
    ),
    right: e.ts ? relTime(e.ts) : null,
  }));

  return (
    <div className="space-y-4">
      {/* Stages of execution */}
      <div className="dh-card px-5 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-xs font-medium text-slate-600"><Cpu className="h-4 w-4 text-brand-500" />Stages of execution</div>
          {cost.total != null && (
            <div className="text-[11px] text-slate-400">{cost.loggedRuns}/{cost.runs} agents logged · {usd(cost.total)} · {((cost.tokens || 0) / 1000).toFixed(1)}k tokens</div>
          )}
        </div>
        <Pipeline steps={stages} />
      </div>

      {/* Agent chain — cohesive timeline */}
      <div className="dh-card p-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-3"><GitBranch className="h-4 w-4 text-brand-500" />Agent chain · execution timeline</div>
        <Timeline events={events} />
      </div>

      {/* Complete profile — every agent's response */}
      <div className="dh-card p-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-1"><Bot className="h-4 w-4 text-violet-500" />Agent responses</div>
        <p className="text-xs text-slate-500 mb-3">Every agent that touched this appointment and what it returned.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {agents.map((a) => (
            <div key={a.key} className="dh-inset p-3.5">
              <div className="flex items-center justify-between gap-2">
                <AgentChip agent={a.key} label={a.name} size="xs" />
                <span className={`pill ${a.mode === 'autopilot' ? 'pill-green' : 'pill-indigo'}`}>{a.mode === 'autopilot' ? 'Autopilot' : 'Copilot'}</span>
              </div>
              <div className="text-sm font-medium text-slate-900 mt-2">{a.title}</div>
              {a.response && <div className="text-xs text-slate-600 mt-0.5 line-clamp-3">{a.response}</div>}
              <div className="mt-2 flex items-center justify-between text-[11px] text-slate-400">
                <div className="flex items-center gap-2">
                  {a.outcome && <StatusPill status={a.outcome} />}
                  {a.confidence != null && <Confidence value={a.confidence} showLabel={false} />}
                </div>
                {(a.costUsd || 0) > 0 && <span className={`tnum ${TONE_TEXT[a.tone] || 'text-slate-400'}`} title={`${(a.tokens || 0).toLocaleString()} tokens`}>{usd4(a.costUsd)}</span>}
              </div>
              {a.ts && <div className="text-[10px] text-slate-300 mt-1">{dateTime(a.ts)}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
